import { useState } from 'react';
import { cn } from '@eriko/ui/src/lib/utils';
import { UsTaxSettings } from './tax/UsTaxSettings';
import { ContractorsSubTab } from './tax/ContractorsSubTab';
import { UkTaxSettings } from './tax/UkTaxSettings';
import { CisSubTab } from './tax/CisSubTab';
import { useOrgSettings } from '../../../hooks/useOrgSettings';

type UsSubTab = 'settings' | 'contractors';
type UkSubTab = 'settings' | 'cis';

export function TaxTab() {
  const { data: orgSettings } = useOrgSettings();
  const jurisdiction = orgSettings?.jurisdiction ?? 'US';

  // Warn + degrade: a connected company in an unsupported country (non-US/UK)
  // has jurisdictionSupported=false — show an honest message instead of the
  // US/UK tax surface, which would be wrong for its filings (OJ-1).
  if (orgSettings && orgSettings.jurisdictionSupported === false) {
    return <UnsupportedJurisdictionNotice />;
  }

  if (jurisdiction === 'UK') {
    return <UkTaxTabContent />;
  }

  return <UsTaxTabContent />;
}

function UnsupportedJurisdictionNotice() {
  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
      <p className="font-semibold text-surface-900">Tax features aren&apos;t available for this organization yet</p>
      <p className="text-surface-700">
        Eriko&apos;s tax tools currently support <strong>US and UK</strong> businesses only. Your connected
        accounting company is in a country we don&apos;t support for tax yet, so tax estimates, codes, and
        filings are turned off. Your bookkeeping, document sync, and reports still work normally.
      </p>
      <p className="text-surface-600">
        If your business is actually US- or UK-based, ask your account owner to set the jurisdiction, or
        contact support.
      </p>
    </div>
  );
}

function UsTaxTabContent() {
  const [subTab, setSubTab] = useState<UsSubTab>('settings');

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b border-surface-200" role="tablist">
        {([
          { key: 'settings' as const, label: 'Tax Settings' },
          { key: 'contractors' as const, label: 'Contractors & 1099' },
        ]).map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={subTab === t.key}
            aria-controls={`tabpanel-${t.key}`}
            onClick={() => setSubTab(t.key)}
            className={cn(
              'px-3 py-2 text-sm border-b-2 -mb-px transition-colors',
              subTab === t.key
                ? 'border-neg text-surface-900 font-semibold'
                : 'border-transparent text-surface-500 font-medium hover:text-surface-900',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`tabpanel-${subTab}`}>
        {subTab === 'settings' && <UsTaxSettings />}
        {subTab === 'contractors' && <ContractorsSubTab />}
      </div>
    </div>
  );
}

function UkTaxTabContent() {
  const [subTab, setSubTab] = useState<UkSubTab>('settings');

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b border-surface-200" role="tablist">
        {([
          { key: 'settings' as const, label: 'Tax Settings' },
          { key: 'cis' as const, label: 'CIS Subcontractors' },
        ]).map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={subTab === t.key}
            aria-controls={`tabpanel-${t.key}`}
            onClick={() => setSubTab(t.key)}
            className={cn(
              'px-3 py-2 text-sm border-b-2 -mb-px transition-colors',
              subTab === t.key
                ? 'border-neg text-surface-900 font-semibold'
                : 'border-transparent text-surface-500 font-medium hover:text-surface-900',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`tabpanel-${subTab}`}>
        {subTab === 'settings' && <UkTaxSettings />}
        {subTab === 'cis' && <CisSubTab />}
      </div>
    </div>
  );
}
